import { useState, useCallback } from 'react'
import { merchantService, creditPurchaseService } from '../services/api'
import { stripeService } from '../services/stripe'
import { useAuth } from './useAuth'

export const useCredits = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const purchaseCredits = useCallback(async (packageId: string) => {
    if (!user?.merchantId) {
      return { success: false, error: 'Not authenticated' }
    }

    try {
      setLoading(true)
      setError(null)

      const packages = stripeService.getCreditPackages()
      const selectedPackage = packages.find(p => p.id === packageId)
      
      if (!selectedPackage) {
        throw new Error('Invalid package')
      }

      const paymentIntent = await stripeService.createPaymentIntent(
        selectedPackage.price * 100,
        'usd',
        {
          merchantId: user.merchantId,
          credits: selectedPackage.credits.toString(),
          packageId
        }
      )

      const purchase = await creditPurchaseService.create({
        merchantId: user.merchantId,
        stripePaymentIntentId: paymentIntent.metadata?.paymentIntentId || 'pending',
        amount: selectedPackage.price,
        creditsReceived: selectedPackage.credits,
        status: 'PENDING',
        purchaseDate: new Date().toISOString()
      })

      const merchant = await merchantService.get(user.merchantId)
      await merchantService.update(user.merchantId, {
        creditBalance: (merchant?.creditBalance || 0) + selectedPackage.credits
      })

      await creditPurchaseService.update(purchase.id, {
        status: 'COMPLETED'
      })

      setLoading(false)
      return { success: true, data: purchase }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to purchase credits'
      setError(errorMessage)
      setLoading(false)
      return { success: false, error: errorMessage }
    }
  }, [user?.merchantId])

  const getBalance = useCallback(async () => {
    if (!user?.merchantId) return 0

    try {
      const merchant = await merchantService.get(user.merchantId)
      return merchant?.creditBalance || 0
    } catch (error) {
      console.error('Failed to get balance:', error)
      return 0
    }
  }, [user?.merchantId])

  const getPurchaseHistory = useCallback(async () => {
    if (!user?.merchantId) return []

    try {
      return await creditPurchaseService.listByMerchant(user.merchantId)
    } catch (error) {
      console.error('Failed to get purchase history:', error)
      return []
    }
  }, [user?.merchantId])

  return {
    loading,
    error,
    purchaseCredits,
    getBalance,
    getPurchaseHistory
  }
}